import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-agentur-kosten";
const TITLE = "Shopify Agentur Kosten vs. Freelancer: Preisvergleich";
const DESCRIPTION =
  "Was kostet eine Shopify Agentur, was ein Freelancer? Preisspannen für Setup, Relaunch und Conversion-Optimierung, und was sich wann rechnet.";

const faqs = [
  {
    q: "Was kostet eine Shopify Agentur?",
    a: "Für laufende Betreuung liegen Agenturen im DACH-Raum meist bei 2.000 bis 8.000 € im Monat, ein Relaunch startet häufig bei 15.000 bis 50.000 €. Stundensätze bewegen sich typischerweise zwischen 100 und 180 €.",
  },
  {
    q: "Was kostet ein Shopify Freelancer?",
    a: "Übliche Stundensätze liegen zwischen 70 und 130 €. Sinnvoller als Stunden ist bei klar umrissenen Vorhaben ein Festpreis, bei mir 3.990 € netto für den Conversion-Sprint über vier Wochen.",
  },
  {
    q: "Wann lohnt sich eine Agentur, wann ein Freelancer?",
    a: "Eine Agentur lohnt sich, wenn viele Disziplinen gleichzeitig gebraucht werden, Ads, Content, Entwicklung, Design. Ein Freelancer ist günstiger und schneller, wenn ein klar abgegrenztes Problem gelöst werden soll, etwa eine zu niedrige Kaufabschlussrate.",
  },
  {
    q: "Warum Festpreis statt Stundensatz?",
    a: "Beim Stundensatz trägt der Kunde das Risiko für Mehraufwand, beim Festpreis der Dienstleister. Zusätzlich verschiebt sich das Gespräch weg von Aufwand hin zum Ergebnis, und genau das soll bezahlt werden.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-agentur-kosten")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Kosten"
      title="Shopify Agentur Kosten vs. Freelancer: Was zahlen Sie wofür?"
      intro="Preise für Shopify-Dienstleistung schwanken enorm, nicht, weil die Arbeit so unterschiedlich ist, sondern weil ganz verschiedene Dinge verkauft werden: Stunden, Kapazität oder ein Ergebnis. Dieser Überblick sortiert die Spannen."
      source="ratgeber-kosten"
      sections={[
        {
          h: "Übliche Preisspannen im DACH-Raum",
          p: [
            "Die folgenden Werte sind Erfahrungswerte aus Angeboten, die mir Shopbetreiber regelmäßig zeigen. Sie ersetzen kein Angebot, geben aber ein Gefühl für die Größenordnung.",
          ],
          list: [
            "Agentur, laufende Betreuung: 2.000 bis 8.000 € pro Monat",
            "Agentur, Relaunch oder Neuaufbau: 15.000 bis 50.000 €",
            "Agentur, Stundensatz: 100 bis 180 €",
            "Freelancer, Stundensatz: 70 bis 130 €",
            "Conversion-Sprint zum Festpreis: 3.990 € netto",
          ],
        },
        {
          h: "Warum Stunden der falsche Maßstab sind",
          p: [
            "Wer Stunden kauft, kauft Aufwand. Für den Shop zählt aber nur der Zustand danach: mehr Umsatz aus demselben Traffic. Zwischen beiden besteht kein zuverlässiger Zusammenhang, eine gute Entscheidung in einer Stunde kann mehr wert sein als vierzig Stunden Umsetzung an der falschen Stelle.",
            "Ein Festpreis zwingt beide Seiten, den Umfang vorher zu klären. Das ist unbequemer im Verkaufsgespräch, aber deutlich planbarer in der Umsetzung.",
          ],
        },
        {
          h: "Wann sich welches Modell rechnet",
          p: [
            "Ob sich Conversion-Arbeit rechnet, hängt von Traffic, Conversion-Rate, Warenkorb, Marge und Aufwand ab. Bei wenig Traffic ist Arbeit an Angebot und Traffic-Qualität meist der bessere erste Hebel.",
            "Rechnen Sie es selbst nach, bevor Sie irgendein Angebot annehmen, der Conversion-Rechner zeigt Ihnen die Größenordnung in einer Minute.",
          ],
        },
        {
          h: "Woran Sie ein seriöses Angebot erkennen",
          p: [
            "Ein belastbares Angebot nennt Zielkennzahlen, Messmethode und Umfang, bevor es über Design spricht. Wer vor der Analyse Ergebnisse garantiert, verkauft ein Produkt und keine Diagnose.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/fragen/shopify-agentur-oder-freelancer", label: "Agentur oder Freelancer?" },
        { to: "/preise", label: "Meine Preise" },
        { to: "/roi-rechner", label: "Potenzial berechnen" },
        { to: "/methode", label: "Wie ich arbeite" },
      ]}
    />
  );
}
